import { useState, useEffect } from 'react'
import { DollarSign, Calendar, Link2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { GoalTypeIcon } from './GoalTypeIcon'
import type { Goal, GoalType, Investment, Debt } from '@/types'
import { GOAL_TYPE_INFO, GOAL_COLORS } from '@/types/goals.types'

interface GoalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  goal?: Goal | null
  investments: Investment[]
  debts: Debt[]
  onSubmit: (data: {
    name: string
    type: GoalType
    targetAmount: number
    deadline: string | null
    color: string
    notes: string
    linkedAccountId: string | null
    linkedAccountType: 'investment' | 'debt' | null
  }) => void
}

export function GoalDialog({
  open,
  onOpenChange,
  goal,
  investments,
  debts,
  onSubmit,
}: GoalDialogProps) {
  const [name, setName] = useState('')
  const [type, setType] = useState<GoalType>('custom')
  const [targetAmount, setTargetAmount] = useState('')
  const [deadline, setDeadline] = useState('')
  const [color, setColor] = useState(GOAL_COLORS[0])
  const [notes, setNotes] = useState('')
  const [linkEnabled, setLinkEnabled] = useState(false)
  const [linkedAccountId, setLinkedAccountId] = useState<string | null>(null)
  const [linkedAccountType, setLinkedAccountType] = useState<'investment' | 'debt' | null>(null)

  const isEditing = !!goal

  // Reset form when dialog opens/closes or goal changes
  useEffect(() => {
    if (open) {
      if (goal) {
        setName(goal.name)
        setType(goal.type)
        setTargetAmount(goal.targetAmount.toString())
        setDeadline(goal.deadline ? goal.deadline.split('T')[0] : '')
        setColor(goal.color)
        setNotes(goal.notes)
        setLinkEnabled(!!goal.linkedAccountId)
        setLinkedAccountId(goal.linkedAccountId)
        setLinkedAccountType(goal.linkedAccountType)
      } else {
        setName('')
        setType('custom')
        setTargetAmount('')
        setDeadline('')
        setColor(GOAL_COLORS[Math.floor(Math.random() * GOAL_COLORS.length)])
        setNotes('')
        setLinkEnabled(false)
        setLinkedAccountId(null)
        setLinkedAccountType(null)
      }
    }
  }, [open, goal])

  // Auto-set account type based on goal type
  useEffect(() => {
    if (type === 'debt_payoff') {
      setLinkedAccountType('debt')
    } else if (linkEnabled && linkedAccountType === 'debt') {
      setLinkedAccountType('investment')
    }
  }, [type, linkEnabled, linkedAccountType])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    onSubmit({
      name,
      type,
      targetAmount: parseFloat(targetAmount) || 0,
      deadline: deadline ? new Date(deadline).toISOString() : null,
      color,
      notes,
      linkedAccountId: linkEnabled ? linkedAccountId : null,
      linkedAccountType: linkEnabled ? linkedAccountType : null,
    })
    
    onOpenChange(false)
  }

  const goalTypes = Object.values(GOAL_TYPE_INFO)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Goal' : 'Create New Goal'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update your financial goal details.' : 'Set a new financial goal to work towards.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Goal Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Goal Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Emergency Fund, Vacation to Hawaii"
              required
            />
          </div>
          
          {/* Goal Type */}
          <div className="space-y-2">
            <Label>Goal Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as GoalType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {goalTypes.map((info) => (
                  <SelectItem key={info.type} value={info.type}>
                    <div className="flex items-center gap-2">
                      <GoalTypeIcon type={info.type} size="sm" />
                      <span>{info.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Target Amount */}
          <div className="space-y-2">
            <Label htmlFor="targetAmount">Target Amount</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="targetAmount"
                type="number"
                min="0"
                step="0.01"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                className="pl-9"
                placeholder="0.00"
                required
              />
            </div>
          </div>
          
          {/* Deadline */}
          <div className="space-y-2">
            <Label htmlFor="deadline">Target Date (Optional)</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="deadline"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          {/* Color Selection */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {GOAL_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${
                    color === c ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          
          {/* Link to Account */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="linkEnabled" className="cursor-pointer">
                  Link to Account
                </Label>
              </div>
              <Switch
                id="linkEnabled"
                checked={linkEnabled}
                onCheckedChange={setLinkEnabled}
              />
            </div>
            
            {linkEnabled && (
              <div className="space-y-2">
                {type === 'debt_payoff' ? (
                  <Select 
                    value={linkedAccountId || ''} 
                    onValueChange={(v) => {
                      setLinkedAccountId(v || null)
                      setLinkedAccountType('debt')
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a debt to track" />
                    </SelectTrigger>
                    <SelectContent>
                      {debts.map((debt) => (
                        <SelectItem key={debt.id} value={String(debt.id)}>
                          {debt.name} ({debt.balance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select 
                    value={linkedAccountId || ''} 
                    onValueChange={(v) => {
                      setLinkedAccountId(v || null)
                      setLinkedAccountType('investment')
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an investment to track" />
                    </SelectTrigger>
                    <SelectContent>
                      {investments.map((inv) => (
                        <SelectItem key={inv.id} value={String(inv.id)}>
                          {inv.symbol} - {inv.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground">
                  Progress will automatically sync with the linked account balance.
                </p>
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details about this goal..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? 'Save Changes' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
